import {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
  type MouseEvent,
} from 'react'
import lodashThrottle from 'lodash.throttle'
import lodashDebounce from 'lodash.debounce'

import {ColumnSize, ResizingColumn} from 'interfaces'
import Logger from 'services/Logger'
import {MOBILE_VIEWPORT_QUERY} from './useMobileViewport'

export interface ResizableTableColumn {
  name: string
  dataKey: string
  widthPercentage: number
  isFixedWidth?: boolean
  isSticky?: boolean
  /** Phones: pinned to the right edge while the table scrolls sideways. */
  isPinnedOnMobile?: boolean
}

interface UseTableResizeNewProps {
  columns: ResizableTableColumn[]
  storageKey: string
  fixedColumnWidths?: Record<string, number>
  additionalOffset?: number
  enablePersistence?: boolean
}

export interface UseTableResizeNewReturn {
  tableRef: (node: HTMLTableElement | null) => void
  columnSizes: ColumnSize[]
  isResizing: boolean
  getColumnWidth: (columnId: string) => string
  handleResizeStart: (
    columnId: string,
    event: MouseEvent<HTMLDivElement>,
  ) => void
  recalculateColumnSizes: () => void
}

function getAvailableTableWidth(table: HTMLTableElement): number {
  let el: HTMLElement | null = table.parentElement
  while (el) {
    const {overflowX} = getComputedStyle(el)
    if (
      overflowX === 'auto' ||
      overflowX === 'scroll' ||
      overflowX === 'hidden'
    ) {
      return window.matchMedia(MOBILE_VIEWPORT_QUERY).matches
        ? Math.max(
            el.clientWidth,
            Number.parseFloat(getComputedStyle(table).minWidth) || 0,
          )
        : el.clientWidth
    }
    el = el.parentElement
  }
  return table.getBoundingClientRect().width
}

export function useTableResizeNew({
  columns,
  storageKey,
  fixedColumnWidths = {},
  additionalOffset = 0,
  enablePersistence = false,
}: UseTableResizeNewProps): UseTableResizeNewReturn {
  const [columnSizes, setColumnSizes] = useState<ColumnSize[]>([])
  const [isResizing, setIsResizing] = useState<boolean>(false)

  // Store the default/minimum widths for each column (based on initial calculation)
  const [defaultColumnWidths, setDefaultColumnWidths] = useState<
    Record<string, number>
  >({})

  const resizingColumnRef = useRef<ResizingColumn | null>(null)
  const activeDragEndRef = useRef<(() => void) | null>(null)
  const tableElRef = useRef<HTMLTableElement | null>(null)
  const isInitializedRef = useRef(false)

  // Persist column sizes to session storage
  const persistColumnSizes = useCallback(
    (sizes: ColumnSize[]) => {
      // A phone layout must never overwrite the user's desktop preferences.
      if (window.matchMedia(MOBILE_VIEWPORT_QUERY).matches) return
      try {
        sessionStorage.setItem(storageKey, JSON.stringify(sizes))
      } catch (error: unknown) {
        Logger.LogError(error as Error)
        console.error('Error persisting column sizes', error)
      }
    },
    [storageKey],
  )

  // Memoize the debounced persisting function
  const debouncedPersistSizes = useMemo(
    () =>
      lodashDebounce((sizes: ColumnSize[]) => persistColumnSizes(sizes), 300),
    [persistColumnSizes],
  )

  /**
   * @description Calculate initial column widths based on table width and percentages
   */
  const calculateColumnSizes = useCallback(() => {
    // Check if table element is defined and prevent accidental recursive calculations
    if (!tableElRef.current || isInitializedRef.current) return

    try {
      // 1. Get available width from scroll container (not table, which can overflow)
      const tableWidth = getAvailableTableWidth(tableElRef.current)

      // 2. Subtract the fixed width columns to get remaining width
      const totalFixedWidth = Object.values(fixedColumnWidths).reduce(
        (sum, width) => sum + width,
        0,
      )

      // Available width for resizable columns after subtracting fixed widths and additional offset
      const availableWidth = tableWidth - totalFixedWidth - additionalOffset

      // 3. Divide available width based on percentage for each column
      const calculatedSizes: ColumnSize[] = []
      const calculatedDefaultWidths: Record<string, number> = {}

      columns.forEach(column => {
        // Skip fixed columns for resizable calculations
        if (fixedColumnWidths[column.dataKey]) {
          calculatedDefaultWidths[column.dataKey] =
            fixedColumnWidths[column.dataKey]
          return
        }

        // Calculate width based on percentage of available width
        const calculatedWidth = (column.widthPercentage / 100) * availableWidth

        calculatedSizes.push({
          id: column.dataKey,
          width: calculatedWidth,
        })

        // 4. Store this calculated width as the default/minimum width
        calculatedDefaultWidths[column.dataKey] = calculatedWidth
      })

      // Set the calculated default widths (these will be the minimum constraints)
      setDefaultColumnWidths(calculatedDefaultWidths)

      // Check for stored sizes in session storage
      const storedSizes = sessionStorage.getItem(storageKey)

      if (storedSizes) {
        try {
          const parsedSizes: ColumnSize[] = JSON.parse(storedSizes)

          // Validate stored sizes against the new default widths
          // Ensure no stored width is smaller than the calculated default width
          const validatedSizes = parsedSizes.map(size => ({
            ...size,
            width: Math.max(size.width, calculatedDefaultWidths[size.id] ?? 0),
          }))

          setColumnSizes(validatedSizes)
        } catch (error: unknown) {
          console.error(
            'Failed to parse stored column sizes, using calculated defaults',
          )
          Logger.LogError(error as Error)
          setColumnSizes(calculatedSizes)
        }
      } else {
        // Use calculated sizes if no stored data
        setColumnSizes(calculatedSizes)
      }

      isInitializedRef.current = true
    } catch (error: unknown) {
      Logger.LogError(error as Error)
      console.error('Error calculating column sizes', error)
    }
  }, [columns, fixedColumnWidths, storageKey, additionalOffset])

  const calculateColumnSizesRef = useRef(calculateColumnSizes)
  calculateColumnSizesRef.current = calculateColumnSizes

  const tableRef = useCallback((node: HTMLTableElement | null) => {
    tableElRef.current = node
    if (!node) {
      isInitializedRef.current = false
      return
    }
    if (!isInitializedRef.current) {
      calculateColumnSizesRef.current()
    }
  }, [])

  /**
   * @description Handles the resizing of a column
   */
  const handleResizeMove = useCallback(
    lodashThrottle((event: globalThis.MouseEvent) => {
      if (!resizingColumnRef.current) return

      const {startX, startWidth, id, widthAdjustmentApplied} =
        resizingColumnRef.current
      const delta = event.clientX - startX
      const newWidth = startWidth + delta

      const columnIndex = columns.findIndex(col => col.dataKey === id)
      if (columnIndex === -1) return

      // 5. Use the default calculated width as the minimum constraint
      const columnMinWidth = defaultColumnWidths[id] || 30

      // Check if we're at or below minimum width (default calculated width)
      if (newWidth <= columnMinWidth && !widthAdjustmentApplied) {
        // Apply minimum width constraint (cannot go below default calculated width)
        setColumnSizes(prevSizes =>
          prevSizes.map(size =>
            size.id === id ? {...size, width: columnMinWidth} : size,
          ),
        )

        resizingColumnRef.current.widthAdjustmentApplied = true
      } else if (newWidth > columnMinWidth) {
        // Allow normal resizing above minimum (can increase beyond default)
        setColumnSizes(prevSizes =>
          prevSizes.map(size =>
            size.id === id ? {...size, width: newWidth} : size,
          ),
        )

        if (resizingColumnRef.current.widthAdjustmentApplied) {
          resizingColumnRef.current.widthAdjustmentApplied = false
        }
      }

      event.preventDefault()
      event.stopPropagation()
    }, 16),
    [columns, defaultColumnWidths],
  )

  /**
   * @description Clean up after column resizing
   */
  const handleResizeEnd = useCallback(() => {
    activeDragEndRef.current = null
    handleResizeMove.cancel()
    // Reset the global pointer style when resizing ends
    document.body.style.cursor = ''

    // Reset the resizing state
    setIsResizing(false)

    // Remove the active-resizing class from the resize handle
    const activeHandles = document.getElementsByClassName('active-resizing')
    while (activeHandles.length) {
      activeHandles[0].classList.remove('active-resizing')
    }

    // Clear the resizing column reference
    resizingColumnRef.current = null

    // Cleanup the added listeners
    document.removeEventListener('mousemove', handleResizeMove)
    document.removeEventListener('mouseup', handleResizeEnd)
  }, [handleResizeMove])

  /**
   * @description Handle the start of a column resize operation
   */
  const handleResizeStart = useCallback(
    (columnId: string, event: MouseEvent<HTMLDivElement>) => {
      if (window.matchMedia(MOBILE_VIEWPORT_QUERY).matches) return
      event.preventDefault()
      if (!tableElRef.current) return

      // Skip if this is a fixed column
      if (fixedColumnWidths[columnId]) return

      // Set isResizing to `true`
      setIsResizing(true)

      // Set the global pointer style to ensure the pointer doesn't change
      document.body.style.cursor = 'col-resize'

      // Add the active-resizing class to the resize handle
      const resizeHandleEl = event.currentTarget as HTMLElement
      resizeHandleEl.classList.add('active-resizing')

      const currentHeader = tableElRef.current.querySelector(
        `th[data-column-id="${columnId}"]`,
      )

      if (!currentHeader) return

      resizingColumnRef.current = {
        id: columnId,
        startX: event.clientX,
        startWidth: currentHeader.getBoundingClientRect().width,
        widthAdjustmentApplied: false,
      }

      // Clear any existing event listeners on the DOM
      document.removeEventListener('mousemove', handleResizeMove)
      document.removeEventListener('mouseup', handleResizeEnd)

      activeDragEndRef.current = handleResizeEnd

      // Attach fresh event listeners on the DOM
      document.addEventListener('mousemove', handleResizeMove)
      document.addEventListener('mouseup', handleResizeEnd)
    },
    [fixedColumnWidths, handleResizeMove, handleResizeEnd],
  )

  /**
   * @description Get width for a specific column
   */
  const getColumnWidth = useCallback(
    (columnId: string) => {
      if (fixedColumnWidths[columnId]) {
        return `${fixedColumnWidths[columnId]}px`
      }

      const column = columnSizes.find(col => col.id === columnId)
      if (!column) {
        return `${defaultColumnWidths[columnId] || 30}px`
      }

      return `${column.width}px`
    },
    [columnSizes, defaultColumnWidths, fixedColumnWidths],
  )

  const recalculateColumnSizes = useCallback(() => {
    isInitializedRef.current = false
    calculateColumnSizesRef.current()
  }, [])

  // Handle zoom/resize events
  useEffect(() => {
    const handleResize = () => {
      isInitializedRef.current = false
      calculateColumnSizesRef.current()
    }

    const debouncedResize = lodashDebounce(handleResize, 250)
    window.addEventListener('resize', debouncedResize)
    return () => {
      window.removeEventListener('resize', debouncedResize)
      debouncedResize.cancel()
    }
  }, [])

  // A breakpoint change can remove a drag handle before mouseup arrives.
  useEffect(() => {
    const query = window.matchMedia(MOBILE_VIEWPORT_QUERY)
    const cancelMobileDrag = () => {
      if (!query.matches) return
      activeDragEndRef.current?.()
      debouncedPersistSizes.cancel()
    }
    query.addEventListener('change', cancelMobileDrag)
    return () => {
      query.removeEventListener('change', cancelMobileDrag)
      activeDragEndRef.current?.()
      debouncedPersistSizes.flush()
    }
  }, [debouncedPersistSizes])

  // Save column sizes when they change
  useEffect(() => {
    if (window.matchMedia(MOBILE_VIEWPORT_QUERY).matches) {
      debouncedPersistSizes.cancel()
      return
    }
    // Only persist if we have valid sizes and it's not the initial render
    if (columnSizes.length && isInitializedRef.current && enablePersistence) {
      debouncedPersistSizes(columnSizes)
    }
  }, [columnSizes, debouncedPersistSizes, enablePersistence])

  return {
    tableRef,
    columnSizes,
    isResizing,
    getColumnWidth,
    handleResizeStart,
    recalculateColumnSizes,
  }
}
